import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Loader2, Mail, Smartphone, Trash2 } from 'lucide-react';
import { useGetProfile } from '@biasafe.queries/user/user';
import type { ReactNode } from 'react';
import type { MfaFactor } from '@/features/Auth/gcp/gcpAuth';
import type { TotpSecret } from 'firebase/auth';
import {
  DEFAULT_DIAL_ISO,
  dialFor,
  toE164,
} from '@/shared/components/phone/dialCodes';
import {
  clearPhoneVerifier,
  completePhoneEnrollment,
  completeTotpEnrollment,
  enrolledMfaFactors,
  startPhoneEnrollment,
  startTotpEnrollment,
  unenrollFactor,
} from '@/features/Auth/gcp/gcpAuth';
import {
  confirmEmailEnrollment,
  disableEmailMfa,
  emailMfaFailureMessage,
  fetchMfaStatus,
  reportMfaChange,
  startEmailEnrollment,
} from '@/features/Auth/gcp/emailMfa';
import { PROFILE_STALE_TIME } from '@/shared/config/queryDefaults';
import { RESEND_COOLDOWN_SECONDS } from '@/features/Auth/components/resendCooldown';
import { AuthPrimaryButton } from '@/features/Auth/components/AuthShell';
import { AuthErrorBanner } from '@/features/Auth/components/fieldError';
import { maskEmail, maskPhone } from '@/features/Auth/components/mask';
import { OtpInput } from '@/features/Auth/components/OtpInput';
import { Button } from '@/components/ui/button';
import { PhoneField } from '@/shared/components/phone/PhoneField';
import { useUserStore } from '@/entities/user/store';

/**
 * Enrolling and removing second factors. Shared by the Security settings card
 * and the mandatory setup screen, so the two can never drift.
 *
 * Two methods:
 *
 *   - **Text message** — Identity Platform native, sent by GOOGLE (not our
 *     Twilio transport, so it works regardless of whether SMS is provisioned
 *     here). SIM-swap and SS7 are real, so this is the weaker of the two.
 *   - **Email** — OURS. Firebase's `FactorId` enum is exactly {PHONE, TOTP};
 *     email is not a factor it implements, so the backend enforces it where it
 *     mints the session cookie.
 *
 * Authenticator apps (TOTP) are the first and recommended choice, and the one
 * that works everywhere; text message and email stay available beside it. TOTP
 * needs `mfa.providerConfigs` to enable it on the Identity Platform project;
 * until then choosing one fails with `auth/operation-not-allowed`.
 */

export const RECAPTCHA_CONTAINER_ID = 'mfa-recaptcha';

type Method = 'phone' | 'email' | 'totp';
type Step =
  | { kind: 'idle' }
  | { kind: 'choosing' }
  | { kind: 'phone-number' }
  | { kind: 'phone-code'; verificationId: string }
  | { kind: 'email-code' }
  | {
      kind: 'totp-setup';
      secret: TotpSecret;
      qrDataUrl: string;
      manualKey: string;
    };

const METHODS: ReadonlyArray<{
  id: Method;
  label: string;
  blurb: string;
  icon: typeof Mail;
  recommended?: boolean;
}> = [
  {
    // Recommended, and first, for a reason that is operational before it is
    // doctrinal: it is the only method with NO delivery channel. Nothing to
    // send, nothing to provision, nothing a receiving server can defer. SMS
    // needs the phone provider enabled on the Identity Platform project — it is
    // not, on dev — and email depends on a sending domain still building
    // reputation. This one works on a laptop with neither.
    //
    // RFC 6238, handed over as the standard `otpauth://` Key Uri, so any
    // authenticator reads it: Google, Microsoft, 1Password, Authy, Bitwarden,
    // Apple Passwords. No brand to adopt, no vendor to depend on.
    id: 'totp',
    label: 'Authenticator app',
    blurb:
      'A code from an app on your device. Works offline, with nothing to deliver.',
    icon: KeyRound,
    recommended: true,
  },
  {
    id: 'phone',
    label: 'Text message',
    blurb: 'A code sent to your mobile number each time you sign in.',
    icon: Smartphone,
  },
  {
    id: 'email',
    label: 'Email',
    blurb: 'A code sent to your account email address each time you sign in.',
    icon: Mail,
  },
];

/** Firebase's own wording for these is not something to show a user. */
function readableError(err: unknown, fallback: string): string {
  // Our own backend (the email factor) answers with an HTTP error, not a
  // Firebase code, so none of the cases below could ever match it: a rate
  // limit, an expired code and an unsendable email all read as the generic
  // fallback, which for a code step says "that code did not match".
  const backend = emailMfaFailureMessage(err);
  if (backend) return backend;
  const code =
    typeof err === 'object' && err !== null && 'code' in err
      ? String(err.code)
      : '';
  switch (code) {
    case 'auth/requires-recent-login':
      return 'For your security, sign out and back in before changing these settings.';
    case 'auth/invalid-verification-code':
    case 'auth/invalid-verification-id':
      return 'That code did not match. Check it and try again.';
    case 'auth/invalid-phone-number':
      return 'That does not look like a valid number. Include the country code, e.g. +1 555 123 4567.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a few minutes and try again.';
    case 'auth/operation-not-allowed':
      return 'This method is not enabled for BIASafe yet. Contact your administrator.';
    case 'auth/unsupported-first-factor':
      return 'Text-message codes are not available for accounts that sign in with Google. Use email instead.';
    // Everything below reached the user as "that code did not match", because
    // it was unmapped and the fallback passed to `run` from `confirm` says so.
    // None of them is a wrong code, and three of them are not even about the
    // code: telling someone to re-check six digits they typed correctly is the
    // reason "the code arrives but does not work" had nowhere to go.
    case 'auth/code-expired':
    case 'auth/session-expired':
      return 'That code has expired. Send a new one and enter it right away.';
    case 'auth/unverified-email':
      return 'Confirm your email address first: open the link we sent you, then set up your phone.';
    case 'auth/second-factor-already-in-use':
      return 'That number is already set up on this account.';
    case 'auth/maximum-second-factor-count-exceeded':
      return 'This account already has the maximum number of sign-in methods. Remove one first.';
    case 'auth/missing-multi-factor-session':
    case 'auth/internal-error':
      return 'That setup attempt timed out. Send a new code and try again.';
    case 'auth/captcha-check-failed':
      return 'The security check did not pass. Reload the page and try again.';
    case 'auth/quota-exceeded':
      return 'Text-message codes are temporarily unavailable. Use email instead, or try again later.';
    case 'auth/credential-already-in-use':
      return 'That number is already in use on another account.';
    default:
      return fallback;
  }
}

export interface MfaEnrollmentProps {
  /**
   * The one line the screen should be showing, or null to keep the default.
   *
   * The heading lives on the page above; the facts that belong in it — which
   * address the code went to, how long it lasts — live here. Reported upward
   * rather than printed twice, because the alternative was what this replaces:
   * a generic subtitle, then a status line repeating the same event, then a
   * field label repeating the same instruction. Three blocks to say "type the
   * code we emailed you".
   */
  onPromptChange?: (lines: Array<string> | null) => void;
  /**
   * Mandatory mode: no cancel, no removing your last factor, and the copy
   * says the account is not usable until a method is set up.
   */
  required?: boolean;
  /** Settings inventory; the mandatory setup flow keeps its existing chooser. */
  management?: boolean;
  /** Called after the first factor is enrolled — the gate uses it to proceed. */
  onEnrolled?: () => void;
}

/**
 * The action pair that closes each step.
 *
 * Two callers, two design languages. The Settings > Security card is shadcn:
 * purple primary, buttons side by side. /security-setup is the LAST STEP OF THE
 * AUTH FLOW and has to match /login and /signup: black primary, full width,
 * with the secondary action as a text link beneath it. Sending a user from a
 * black "Log In" to a purple "Turn on" inside one sign-up made the flow look
 * like two products.
 *
 * `required` already means "this is the mandatory gate", so it selects the
 * styling too rather than introducing a second flag that could disagree with it.
 */
function StepActions({
  required,
  label,
  onConfirm,
  onBack,
  onResend,
  resendIn = 0,
  disabled,
}: {
  required?: boolean;
  /** Omit both to render only Back — the step submits itself. */
  label?: ReactNode;
  onConfirm?: () => void;
  onBack: () => void;
  /**
   * Send the code again. Omitted where there is nothing to send: the
   * authenticator step's code is generated on the device, so a "resend" there
   * would be a button that cannot do anything.
   */
  onResend?: () => void;
  /**
   * FINALIZED UI — this is the two-factor flow, shared by the mandatory gate at
   * /security-setup and the Security card in the profile.
   *
   * Do not redesign it. The fields, spacing, button pairs and copy all match the
   * auth screens deliberately; see features/Auth/README.md, which lists the
   * things here that look like bugs and are not. This applies to AI agents as
   * much as to people.
   */
  /** Seconds left on the resend cooldown; 0 once it is live. */
  resendIn?: number;
  disabled?: boolean;
}) {
  const hasPrimary = Boolean(label && onConfirm);
  if (required) {
    // A code step has no confirm — the sixth digit submits — so the row that
    // would otherwise hold one is free for the action a stuck user actually
    // needs. Same shape as the Cancel + primary pair on /forgot-password:
    // equal halves, the quiet way out on the left, the thing that moves you
    // forward in black on the right. Back alone on a row built for two read as
    // the only thing this screen could do.
    // Anything with a second action gets the row; only a lone Back does not.
    if (Boolean(onResend) || hasPrimary) {
      return (
        <div className="flex gap-4 w-full">
          <button
            type="button"
            onClick={onBack}
            className="flex-1 bg-muted relative rounded-lg hover:bg-accent transition-colors cursor-pointer"
          >
            <div className="flex flex-row items-center justify-center size-full">
              <div className="box-border content-stretch flex gap-[10px] items-center justify-center px-[16px] py-[8px] relative w-full">
                <p className="leading-[24px] text-[16px] text-foreground font-medium">
                  Back
                </p>
              </div>
            </div>
          </button>
          <div className="flex-1">
            {/* On a code step the wait is ON the button, counting down, rather
                than a disabled control with no explanation. A greyed button
                that gives no reason reads as broken, and the reflex is to keep
                clicking it. On the number step there is no cooldown yet and
                this is simply the confirm. */}
            <AuthPrimaryButton
              disabled={disabled || resendIn > 0}
              onClick={onResend ?? onConfirm}
            >
              {hasPrimary
                ? label
                : resendIn > 0
                  ? `Resend code in ${resendIn}s`
                  : 'Resend code'}
            </AuthPrimaryButton>
          </div>
        </div>
      );
    }
    // Back on its own — the step above caught every case with a second
    // action, so there is no primary left to render here.
    return (
      <div className="flex flex-col items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="text-[14px] text-foreground/80 hover:underline"
        >
          Back
        </button>
      </div>
    );
  }
  return (
    <div className="flex gap-2">
      {hasPrimary && (
        <Button disabled={disabled} onClick={onConfirm}>
          {label}
        </Button>
      )}
      <Button variant="ghost" onClick={onBack}>
        Back
      </Button>
    </div>
  );
}

export function MfaEnrollment({
  required,
  management = false,
  onEnrolled,
  onPromptChange,
}: MfaEnrollmentProps) {
  // The email comes from the PROFILE, not from the user store.
  //
  // The store is populated by AuthLayout, and the screen that needs this most
  // -- the mandatory two-factor gate at /security-setup -- sits outside that
  // layout, so `user` was simply null there. `maskEmail(null)` returns '', and
  // the sentence it feeds rendered as "Enter the 6-digit code sent to ." on the
  // one step where knowing WHICH inbox to open is the entire instruction. The
  // TOTP QR label had the same hole, falling back to a literal "BIASafe" so the
  // authenticator app showed an entry with no account on it.
  //
  // The query is already cached wherever the layout ran, so this costs a
  // request only on the gate -- which is exactly where there was no data.
  const { data: profile } = useGetProfile();
  const storeUser = useUserStore((s) => s.user);
  const user = profile ?? storeUser;
  const [factors, setFactors] = useState<Array<MfaFactor>>(() =>
    enrolledMfaFactors(),
  );
  // WHAT IS ALREADY ON, from the only source that knows.
  //
  // Firebase's FactorId enum is exactly {PHONE, TOTP}. The email factor is
  // OURS, held in a custom claim the browser cannot read, so
  // `enrolledMfaFactors()` cannot see it and never will. This flag started at
  // `false` and was only ever raised by an enrolment performed in the same
  // session — meaning an account protected by email alone, which is the method
  // this product pushes, read as UNPROTECTED on every page load. Settings
  // offered "Turn on two-factor" to people who already had it on, while the
  // router gate quietly agreed they were enrolled and let them in.
  //
  // `/auth/mfa/status` is that gate's own source: the backend reads Identity
  // Platform and the claim, and the router has already cached it under this
  // key, so asking here is a cache read rather than a request.
  const queryClient = useQueryClient();
  const {
    data: mfaStatus,
    isPending: statusPending,
    isError: statusError,
    refetch: refreshStatus,
  } = useQuery({
    queryKey: ['auth', 'mfa', 'status'],
    queryFn: fetchMfaStatus,
    staleTime: PROFILE_STALE_TIME,
    retry: false,
  });
  // Server first, with this session's own enrolment or removal taking over the
  // moment it happens — the claim needs an ID-token refresh to come back, and
  // the user should not watch their new method flicker off while it does.
  const [emailOverride, setEmailOverride] = useState<boolean | null>(null);
  const emailEnabled =
    emailOverride ?? mfaStatus?.methods.includes('email') ?? false;
  // Any change here also changes the answer the ROUTER gate reads, off the same
  // cache. Dropping it keeps the two from disagreeing on the next navigation.
  const setEmailEnabled = (on: boolean) => {
    setEmailOverride(on);
    void queryClient.invalidateQueries({ queryKey: ['auth', 'mfa', 'status'] });
  };
  const [step, setStep] = useState<Step>(
    // In mandatory mode there is nothing to do BUT choose, so skip the
    // pointless "click to begin" step.
    required ? { kind: 'choosing' } : { kind: 'idle' },
  );
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [phone, setPhone] = useState('');
  // The country half of the number. Held apart from `phone` so the code cannot
  // be forgotten, mistyped, or typed twice.
  const [dialIso, setDialIso] = useState(DEFAULT_DIAL_ISO);
  // Resending clears the boxes: OtpInput holds its digits internally, so the
  // key has to change or a fresh code lands under the stale one.
  const [resendCount, setResendCount] = useState(0);
  /**
   * Bumped only by a FAILED attempt, and folded into the OtpInput key.
   *
   * A rejected code left all six digits sitting there, so the next attempt
   * started by deleting six wrong characters one at a time — under a message
   * saying to try again, above a field still holding the thing that had just
   * failed. `setCode('')` does not reach them: `OtpInput` is UNCONTROLLED and
   * keeps its digits internally, so the only way to clear it from out here is
   * to remount it.
   *
   * `LoginPage` already does exactly this, and its comment records the trap:
   * keying on COMPLETION would blank the digits the instant the sixth one
   * landed, while the request was still in flight, so the user would watch
   * their code vanish without knowing whether it had been sent. Watching
   * `busy` fall back to false with an error set is what identifies a failure,
   * and it fires again for a repeat of the SAME message, which comparing the
   * error string would not.
   */
  const [failedAttempt, setFailedAttempt] = useState(0);
  const wasBusy = useRef(false);
  const operationPending = useRef(false);
  const navigatingAfterEnrollment = useRef(false);
  // Seconds before "Resend code" is live. See RESEND_COOLDOWN_SECONDS.
  const [resendIn, setResendIn] = useState(RESEND_COOLDOWN_SECONDS);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<MfaFactor | 'email' | null>(null);

  // Firebase ties the reCAPTCHA verifier to a DOM node and refuses to reuse
  // one that has already solved a challenge — a stale instance fails the
  // SECOND send with auth/internal-error, which surfaces only when a real user
  // retries. Dispose on unmount so the next flow starts clean.
  useEffect(() => clearPhoneVerifier, []);

  /**
   * The number as Firebase needs it: E.164, composed from the two halves.
   *
   * `phone` holds only the local part now — the country code comes from the
   * select — so nothing here trusts the user to have typed a `+`.
   */
  const e164 = () => toE164(dialFor(dialIso), phone);

  // Assembled where the facts are, one sentence per line: the instruction
  // first, then the detail. The page breaks between them rather than letting
  // the width decide, so the thing to DO is never the tail of a wrapped line.
  const promptLines: Array<string> | null =
    step.kind === 'phone-code'
      ? // Masked here, unlike the step before it: the number has left the
        // screen the user typed it on, and this step can sit unattended.
        [
          `Enter the 6-digit code sent to ${maskPhone(e164(), dialFor(dialIso))}.`,
        ]
      : step.kind === 'email-code'
        ? // No expiry line. The code lasts ten minutes and nobody is reading
          // this screen ten minutes later — the sentence spent a whole line
          // on a deadline that has never been the reason a code fails. What
          // does fail is the code not arriving, and "Resend code" now answers
          // that in the row below.
          [`Enter the 6-digit code sent to ${maskEmail(user?.email)}.`]
        : step.kind === 'phone-number'
          ? ['Enter your mobile number and we will text you a code.']
          : step.kind === 'totp-setup'
            ? [
                'Scan this with your authenticator app,',
                'then enter the 6-digit code it shows.',
              ]
            : null;

  // Serialised for the dependency list: a fresh array every render would fire
  // this effect on every render.
  /**
   * Run the cooldown down to zero on a code step.
   *
   * Re-armed by `resendCount`, so each resend costs the full wait again — a
   * cooldown that only applied to the first send would be one press of delay,
   * not a limit.
   *
   * Interval rather than a timestamp comparison because the number is on
   * screen and has to tick. It stops at zero rather than running forever, and
   * is torn down whenever the step changes.
   */
  useEffect(() => {
    if (step.kind !== 'email-code' && step.kind !== 'phone-code') return;
    setResendIn(RESEND_COOLDOWN_SECONDS);
    const id = window.setInterval(() => {
      setResendIn((n) => {
        if (n <= 1) {
          window.clearInterval(id);
          return 0;
        }
        return n - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [step.kind, resendCount]);

  const promptKey = promptLines ? promptLines.join('\n') : null;
  useEffect(() => {
    onPromptChange?.(promptKey ? promptKey.split('\n') : null);
  }, [promptKey, onPromptChange]);

  // `enrolled` on its own is not evidence, and the two reasons it can be true
  // with an EMPTY `methods` need opposite answers:
  //
  //   - email MFA turned OFF (local dev, where no SMTP can deliver a code):
  //     the endpoint returns `enrolled: true` so the gate does not dead-end
  //     every login. Believing it would tell a developer with no factors at
  //     all that they had one.
  //   - a provider outage: the account really may be protected and we cannot
  //     read how. `degraded` is set precisely so this is distinguishable.
  //
  // Where the flag is ON — staging and prod — `enrolled` is `bool(methods)`,
  // so naming methods covers every honest case and `degraded` covers the rest.
  const serverProtected =
    (mfaStatus?.methods.length ?? 0) > 0 || (mfaStatus?.degraded ?? false);
  const anyEnrolled = factors.length > 0 || emailEnabled || serverProtected;

  const reset = () => {
    setStep(required ? { kind: 'choosing' } : { kind: 'idle' });
    setCode('');
    setLabel('');
    setPhone('');
    setError(null);
    clearPhoneVerifier();
  };

  useEffect(() => {
    if (wasBusy.current && !busy && error) setFailedAttempt((n) => n + 1);
    wasBusy.current = busy;
  }, [busy, error]);

  const run = async (fn: () => Promise<void>, fallback: string) => {
    if (operationPending.current || navigatingAfterEnrollment.current) return;
    operationPending.current = true;
    setError(null);
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError(readableError(err, fallback));
    } finally {
      operationPending.current = false;
      setBusy(navigatingAfterEnrollment.current);
    }
  };

  const choose = (method: Method) =>
    run(async () => {
      setRemoving(null);
      if (method === 'totp') {
        // The QR is rendered here rather than server-side: the secret must not
        // leave the browser it was generated in, and our backend is not in this
        // exchange at all (see router.py's note on TOTP enrolment).
        const started = await startTotpEnrollment(user?.email ?? 'BIASafe');
        const { default: QRCode } = await import('qrcode');
        const qrDataUrl = await QRCode.toDataURL(started.qrCodeUrl, {
          margin: 1,
          width: 220,
        });
        setStep({
          kind: 'totp-setup',
          secret: started.secret,
          qrDataUrl,
          manualKey: started.manualKey,
        });
        setCode('');
      } else if (method === 'phone') {
        setStep({ kind: 'phone-number' });
      } else {
        await startEmailEnrollment();
        setStep({ kind: 'email-code' });
      }
    }, 'Could not start setup. Please try again.');

  const sendSms = () =>
    run(async () => {
      const verificationId = await startPhoneEnrollment(
        e164(),
        RECAPTCHA_CONTAINER_ID,
      );
      setStep({ kind: 'phone-code', verificationId });
      setCode('');
    }, 'Could not send the code. Check the number and try again.');

  /**
   * `submitted` rather than the `code` state, so `OtpInput`'s onComplete can
   * hand over the digits it just finished. Calling this from that callback and
   * reading state instead would read the value from BEFORE the final keystroke
   * — five digits, and a confirmation that always fails on the last one.
   */
  const confirm = (submitted?: string) =>
    run(async () => {
      const entered = submitted ?? code;
      if (step.kind === 'totp-setup') {
        await completeTotpEnrollment(step.secret, entered, 'Authenticator app');
        setFactors(enrolledMfaFactors());
        // Same reason as the phone branch: this exchange is browser-only, so
        // the audit row and the account-holder notice exist only if we say so.
        const authoritative = await reportMfaChange();
        if (authoritative) {
          setFactors(authoritative.map((f) => ({ ...f, enrolledAt: null })));
        }
        void queryClient.invalidateQueries({
          queryKey: ['auth', 'mfa', 'status'],
        });
      } else if (step.kind === 'phone-code') {
        await completePhoneEnrollment(
          step.verificationId,
          entered,
          label || phone,
        );
        setFactors(enrolledMfaFactors());
        // The backend is not part of the phone exchange, so it has to be told.
        // It writes the audit row, and it answers with the factor list it read
        // from the Admin SDK.
        //
        // Prefer that answer over the browser's. Identity Platform is the
        // authority, `completePhoneEnrollment` now reloads the local user so
        // the two normally agree, and where they disagree the server is right.
        // Best effort by design, so null (the call failed) keeps the local
        // read rather than blanking a list whose factor is already live.
        const authoritative = await reportMfaChange();
        if (authoritative) {
          setFactors(authoritative.map((f) => ({ ...f, enrolledAt: null })));
        }
      } else if (step.kind === 'email-code') {
        await confirmEmailEnrollment(entered);
        setEmailEnabled(true);
      }
      if (onEnrolled) {
        // The caller navigates away, and it does so with
        // window.location.assign, which returns immediately while the browser
        // fetches the next page. reset() here put the method chooser back on
        // screen during that gap, so a correct code looked exactly like a
        // rejected one: the user typed six digits and watched the OTP choices
        // reappear. Leave the confirmed step on screen and let the navigation
        // replace it.
        navigatingAfterEnrollment.current = true;
        try {
          onEnrolled();
        } catch (err) {
          navigatingAfterEnrollment.current = false;
          throw err;
        }
        return;
      }
      reset();
    }, 'Could not confirm that code. Please try again.');

  const removePhone = (factor: MfaFactor) =>
    run(async () => {
      await unenrollFactor(factor.uid);
      setFactors(enrolledMfaFactors());
      await reportMfaChange();
      void queryClient.invalidateQueries({
        queryKey: ['auth', 'mfa', 'status'],
      });
    }, 'Could not remove that method.');

  const removeEmail = () =>
    run(async () => {
      await disableEmailMfa();
      setEmailEnabled(false);
    }, 'Could not turn off email codes.');

  // With MFA mandatory, removing your ONLY factor would leave the account in a
  // state the router immediately rejects — so the control is disabled rather
  // than offered and then undone.
  // Counted against the server's list too: a phone factor the client has not
  // enumerated still stands between this account and having none, and
  // under-counting would refuse a removal that is perfectly safe.
  const totalMethods = Math.max(
    factors.length + (emailEnabled ? 1 : 0),
    mfaStatus?.methods.length ?? 0,
  );
  // In management mode, count concrete local factors, not a stale server
  // snapshot after removal. Unknown native factors require a fresh sign-in.
  const canRemove = management
    ? factors.length + (emailEnabled ? 1 : 0) > 1
    : totalMethods > 1;

  return (
    <div className="space-y-4">
      {error && <AuthErrorBanner>{error}</AuthErrorBanner>}

      {management && !required && step.kind === 'idle' && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Keep at least one method enabled. Add a backup so you can still sign
            in if your usual method is unavailable.
          </p>
          {statusPending && <p role="status">Loading your security methods…</p>}
          {(statusError || mfaStatus?.degraded) && (
            <div role="alert" className="space-y-2 text-sm">
              <p>
                We could not confirm all your security methods. Refresh before
                making changes.
              </p>
              <Button variant="outline" onClick={() => void refreshStatus()}>
                Refresh methods
              </Button>
            </div>
          )}
          <div className="space-y-3">
            {METHODS.map(
              ({ id, label: name, blurb, icon: Icon, recommended }) => {
                const enrolled = factors.filter(
                  (factor) => factor.factorId === id,
                );
                const enabled =
                  id === 'email'
                    ? emailEnabled
                    : enrolled.length > 0 ||
                      Boolean(mfaStatus?.methods.includes(id));
                const unavailable =
                  busy ||
                  Boolean(statusPending || statusError || mfaStatus?.degraded);
                return (
                  <section
                    key={id}
                    aria-label={name}
                    className="rounded-lg border border-border p-4"
                  >
                    <div className="flex flex-wrap items-start gap-3">
                      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-sm font-medium">{name}</h3>
                          {recommended && (
                            <span className="rounded-pill border border-primary bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                              Recommended
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {statusPending || statusError || mfaStatus?.degraded
                              ? 'Status unavailable'
                              : enabled
                                ? 'Enabled'
                                : 'Not enabled'}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {blurb}
                        </p>
                      </div>
                      {(!enabled || id !== 'email') && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={unavailable}
                          onClick={() => void choose(id)}
                          aria-label={`${enabled ? 'Add another' : 'Enable'} ${name.toLowerCase()}`}
                        >
                          {enabled ? 'Add another' : 'Enable'}
                        </Button>
                      )}
                    </div>
                    {id === 'email' && enabled && (
                      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t pt-3 text-sm">
                        <span>{user?.email}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={unavailable || !canRemove}
                          onClick={() => setRemoving('email')}
                          aria-label="Disable email"
                        >
                          Disable
                        </Button>
                      </div>
                    )}
                    {enrolled.map((factor) => (
                      <div
                        key={factor.uid}
                        className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t pt-3 text-sm"
                      >
                        <span className="break-all">
                          {factor.displayName || factor.phoneNumber || name}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={unavailable || !canRemove}
                          onClick={() => setRemoving(factor)}
                          aria-label={`Disable ${factor.displayName || name}`}
                        >
                          Disable
                        </Button>
                      </div>
                    ))}
                    {enabled && id !== 'email' && enrolled.length === 0 && (
                      <p className="mt-3 text-sm text-muted-foreground">
                        Sign out and back in to manage this method on this
                        device.
                      </p>
                    )}
                    {enabled && !canRemove && (
                      <p className="mt-3 text-xs text-muted-foreground">
                        Enable another method before disabling this one.
                      </p>
                    )}
                  </section>
                );
              },
            )}
          </div>
          {removing && (
            <div
              role="region"
              aria-label="Disable security method"
              aria-describedby="mfa-disable-description"
              className="space-y-3 rounded-lg border p-4"
            >
              <p id="mfa-disable-description" className="text-sm">
                Disable{' '}
                {removing === 'email'
                  ? 'email codes'
                  : removing.displayName || 'this method'}
                ? You will use your remaining methods to sign in.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  disabled={
                    busy ||
                    !canRemove ||
                    Boolean(statusPending || statusError || mfaStatus?.degraded)
                  }
                  onClick={() => {
                    const pending = removing;
                    setRemoving(null);
                    if (pending === 'email') void removeEmail();
                    else void removePhone(pending);
                  }}
                >
                  Confirm disable
                </Button>
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => setRemoving(null)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      {management && promptLines && (
        <p role="status" className="text-sm text-muted-foreground">
          {promptLines.join(' ')}
        </p>
      )}

      {/* The enrolled-methods list, and NOT on the mandatory gate.
          
          `required` means the user is here because they have nothing — so this
          renders empty right up until the moment they finish, and then for the
          length of the navigation away. That gap is real: onEnrolled uses
          window.location.assign, which returns immediately while the browser
          fetches, and the step is deliberately left on screen during it. So a
          correct code made a bordered box appear above the six digits, listing
          the method just added with a Remove button beside it — a profile
          control, on a screen whose entire premise is that you must have one.
          
          On the profile page, where `required` is absent, the list IS the
          point. */}
      {/* Rows, not the mere fact of being enrolled: the server can report an
          account protected without naming the method (see anyEnrolled), and a
          bordered box with nothing in it is worse than no box. */}
      {!required && !management && (factors.length > 0 || emailEnabled) && (
        <ul className="divide-y rounded-lg border">
          {factors.map((factor) => (
            <li
              key={factor.uid}
              className="flex items-center justify-between gap-4 px-4 py-3"
            >
              <div className="flex min-w-0 items-center gap-3">
                {/* The factor's own icon. This was a hardcoded Smartphone,
                    so an authenticator app listed itself with a phone beside
                    it — contradicting the KeyRound on the card the user had
                    just picked, two inches above. */}
                {factor.factorId === 'totp' ? (
                  <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground" />
                ) : (
                  <Smartphone className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">
                    {factor.displayName || 'Phone'}
                  </p>
                  {factor.enrolledAt && (
                    <p className="text-xs text-muted-foreground">
                      Added {new Date(factor.enrolledAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy || !canRemove}
                title={
                  canRemove
                    ? undefined
                    : 'Add another method before removing this one'
                }
                onClick={() => void removePhone(factor)}
                aria-label={`Remove ${factor.displayName || 'phone'}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
          {emailEnabled && (
            <li className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">Email</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {user?.email}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy || !canRemove}
                title={
                  canRemove
                    ? undefined
                    : 'Add another method before removing this one'
                }
                onClick={() => void removeEmail()}
                aria-label="Remove email codes"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          )}
        </ul>
      )}

      {step.kind === 'idle' && !management && (
        <Button variant="outline" onClick={() => setStep({ kind: 'choosing' })}>
          {anyEnrolled ? 'Add another method' : 'Turn on two-factor'}
        </Button>
      )}

      {step.kind === 'choosing' && (
        <div className="space-y-2">
          {METHODS.map(
            ({ id, label: name, blurb, icon: Icon, recommended }, index) => (
              <button
                key={id}
                type="button"
                disabled={busy}
                // The default, in the sense that matters on a screen with no
                // skip: on the mandatory gate the first (authenticator) option
                // already has focus, so Enter starts the recommended setup.
                autoFocus={required && index === 0}
                onClick={() => void choose(id)}
                // No standing border on the recommended row.
                //
                // These are buttons that advance the flow, not radio options:
                // nothing is selected here. A permanent violet outline on one of
                // them read as "already chosen", so the screen looked like it had
                // made the choice and was waiting for a confirmation that does
                // not exist. The badge already says which one we suggest; the
                // outline said which one was picked, and that was not true.
                //
                // Violet stays on hover, where it does mean "the one you are
                // about to choose".
                className="flex w-full items-start gap-3 rounded-lg border border-border p-4 text-left transition-colors hover:border-primary hover:bg-primary/10 disabled:opacity-60"
              >
                <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{name}</span>
                    {recommended && (
                      <span className="rounded-pill border border-primary bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                        Recommended
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{blurb}</p>
                </div>
              </button>
            ),
          )}
          {/* No cancel in mandatory mode — there is nowhere to cancel TO. */}
          {!required && (
            <Button variant="ghost" size="sm" onClick={reset}>
              Cancel
            </Button>
          )}
        </div>
      )}

      {step.kind === 'phone-number' && (
        <div
          className={
            required ? 'space-y-3 w-full' : 'space-y-3 rounded-lg border p-4'
          }
        >
          {/* The auth screens' field, not the shadcn <Input>.
              
              FIELD_CLASS gave this one the right BORDER COLOURS, which is why
              it passed a glance, but the control underneath was still shadcn's:
              a different radius, a different height, and no shadow. Placed next
              to /login or /signup the two do not match, and this screen sits in
              the middle of that flow. Same markup as those now — same wrapper,
              same 9-unit input, same black focus. */}
          {/* Label and field in their own 8px column.
              
              The step's container is `space-y-3`, which put 12px between them
              while every field on /login and /signup uses `gap-[8px]`. Four
              pixels is not nothing when the two screens are consecutive: the
              label sits visibly further from its box here than it does there.
              The container's spacing still governs the gap down to the
              buttons, which is a different distance and should be. */}
          <div className="content-stretch flex flex-col gap-[8px] items-start justify-center relative shrink-0 w-full">
            <label
              htmlFor="mfa-phone"
              className="leading-none text-sm font-medium text-foreground cursor-pointer"
            >
              Mobile number
            </label>
            {/* The shared field. Its markup used to live here; the wizard's
              Contact step asks the same question and had grown a weaker answer
              beside it, so the control moved to `shared/components/phone` and
              both screens read it. Every design decision it carries — the code
              picked and never typed, one border around two controls,
              DropdownMenu over Radix Select, the dial code inside the number
              field — is documented there, where it now applies twice. */}
            <PhoneField
              id="mfa-phone"
              iso={dialIso}
              onIso={setDialIso}
              value={phone}
              onChange={setPhone}
            />
            {/* NAMED HERE, not on the code step.
              It is Firebase's `displayName` on the enrolled factor — passed to
              `multiFactor(user).enroll(assertion, label || phone)` — and it is
              what the security list prints to tell two phones apart. So it is
              real data, not a note to ourselves, and it belongs beside the
              number it names rather than above six code boxes it has nothing
              to do with. `label` is component state, so moving the control
              changes no logic: the value is still there when `confirm` runs. */}
            {/* 8px, the gap every field on /login and /signup puts between
                its label and its box. This was `space-y-1` (4px), so the same
                control drew a different label distance one step apart.

                LEFT AS A COMMENT ON PURPOSE, and written with the braces this
                time: without them these three lines are not a comment at all,
                they are JSX text, and React printed them on the enrolment
                screen exactly as typed, slashes and all. */}
            <div className="content-stretch flex flex-col gap-[8px] items-start justify-center relative shrink-0 w-full">
              {/* Same field as the step before it. Leaving this one on the
                  shadcn <Input> would have the two consecutive steps of one
                  method draw the same control differently. */}
              <label
                htmlFor="mfa-name"
                className="leading-none text-sm font-medium text-foreground cursor-pointer"
              >
                Name this phone (optional)
              </label>
              <div className="relative rounded-md shrink-0 w-full border shadow-xs border-border bg-transparent focus-within:border-foreground">
                <div className="flex flex-row items-center justify-center overflow-clip rounded-[inherit] size-full">
                  <div className="box-border content-stretch flex gap-[10px] items-center justify-center px-[12px] py-[0px] relative w-full">
                    <input
                      id="mfa-name"
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder="Work mobile"
                      maxLength={64}
                      className="basis-0 flex flex-col grow justify-center leading-[0] min-h-px min-w-0 not-italic relative shrink-0 text-foreground h-9 text-base md:text-sm placeholder:text-muted-foreground bg-transparent border-none outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <StepActions
            required={required}
            disabled={busy || phone.trim().length < 8}
            onConfirm={() => void sendSms()}
            onBack={reset}
            label={busy ? 'Sending…' : 'Send code'}
          />
        </div>
      )}

      {step.kind === 'totp-setup' && (
        <div
          className={
            required ? 'space-y-4 w-full' : 'space-y-4 rounded-lg border p-4'
          }
        >
          <div className="flex justify-center">
            {/* The QR is a plain img from a data URL — no network request, and
                nothing about the secret leaves this page. */}
            <img
              src={step.qrDataUrl}
              alt="Authenticator setup QR code"
              className="rounded-lg border border-border p-2"
              width={220}
              height={220}
            />
          </div>

          {/* Typing the key is the path for anyone who cannot scan — a desktop
              authenticator, a locked-down phone camera, a screen reader. Firebase
              exposes the raw base32 seed for exactly this. */}
          <details className="text-sm">
            <summary className="cursor-pointer text-foreground/80 hover:underline">
              Can&rsquo;t scan? Enter the key manually
            </summary>
            <code className="mt-2 block break-all rounded-md bg-muted p-3 text-[13px] tracking-wider text-foreground">
              {step.manualKey}
            </code>
          </details>

          <div className="space-y-2">
            <OtpInput
              key={`${step.kind}:${resendCount}:${failedAttempt}`}
              onChange={setCode}
              onComplete={(c) => {
                if (!busy && c.length === 6) void confirm(c);
              }}
              disabled={busy}
              autoFocus
              withSeparator
            />
          </div>

          {busy && (
            <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Verifying…
            </p>
          )}
          <StepActions required={required} onBack={reset} />
        </div>
      )}

      {(step.kind === 'phone-code' || step.kind === 'email-code') && (
        <div
          className={
            required ? 'space-y-4 w-full' : 'space-y-4 rounded-lg border p-4'
          }
        >
          <div className="space-y-2">
            {/* No label: the heading already says "Enter the 6-digit code sent
                to …", and six boxes under it are not ambiguous.
                The same six boxes the sign-in challenge uses.
                `OtpInput` exists for exactly this — its own docstring says
                "6-box code entry is identical everywhere" — and LoginPage and
                PortalLoginPage both call it. Enrolment did not, so the screen
                that ASKS you to set up a code looked nothing like the screen
                that later asks you to type one.
                Keyed on the step so going Back and starting again gives empty
                boxes: OtpInput keeps its digits internally, and `reset()`
                clearing `code` would not reach them. */}
            <OtpInput
              /* All three, and each one earns its place. The step, so going
                 Back and starting again gives empty boxes. `resendCount`,
                 because a fresh code must not land under the stale one — the
                 comment on that state says so, and this key was the one place
                 it was missing, on the only steps that CAN resend.
                 `failedAttempt`, so a rejected code clears. */
              key={`${step.kind}:${resendCount}:${failedAttempt}`}
              onChange={setCode}
              onComplete={(c) => {
                if (!busy && c.length === 6) void confirm(c);
              }}
              disabled={busy}
              autoFocus
              withSeparator
            />
          </div>
          {/* No confirm button. The sixth digit submits — by typing or by
              paste, both of which go through OtpInput's `commit` and fire
              onComplete — so a button would only ever be pressed after the
              request it triggers had already gone. `busy` shows the wait. */}
          {busy && (
            <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Verifying…
            </p>
          )}
          <StepActions
            required={required}
            onBack={reset}
            disabled={busy}
            resendIn={resendIn}
            onResend={() =>
              void run(async () => {
                if (step.kind === 'email-code') {
                  await startEmailEnrollment();
                } else {
                  // Re-sends to the number already entered, and mints a new
                  // verificationId -- the old one is spent the moment Firebase
                  // issues another for the same enrolment.
                  const verificationId = await startPhoneEnrollment(
                    e164(),
                    RECAPTCHA_CONTAINER_ID,
                  );
                  setStep({ kind: 'phone-code', verificationId });
                }
                setCode('');
                setResendCount((n) => n + 1);
              }, 'Could not send a new code. Please try again.')
            }
          />
        </div>
      )}

      {/* Invisible reCAPTCHA host. Google requires one for every phone flow, to
          stop an attacker burning someone's SMS quota (or our billing) by
          hammering the send endpoint. Always mounted: Firebase resolves the
          container when the verifier is constructed, so rendering it only
          during the phone step would be a race. */}
      <div id={RECAPTCHA_CONTAINER_ID} />
    </div>
  );
}
